import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Trash2, Loader2, ShoppingBag, ArrowRight, CreditCard, Minus, Plus } from 'lucide-react';
import { FaCcStripe, FaPaypal } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CUSHION_THUMBNAILS = {
  'bay-window-bench-seat-cushion': '/assets/thumbnails/bay-window-bench-seat-cushion.png',
  'rectangle-bench-seat-cushion': '/assets/thumbnails/rectangle-bench-seat-cushion.png',
  'bench-seat': '/assets/thumbnails/rectangle-bench-seat-cushion.png',
  'round-bench-seat-cushion': '/assets/thumbnails/round-bench-seat-cushion.png',
  'wedge-back-cushion': '/assets/thumbnails/wedge-back-cushion.png',
  'rounded-corners-bench-seat-cushion': '/assets/thumbnails/rounded-corners-bench-seat-cushion.png',
  'chair-bench-seat-cushion': '/assets/thumbnails/chair-bench-seat-cushion.png',
  'round-back-chair-seat-cushion': '/assets/thumbnails/round-back-chair-seat-cushion.png',
  'round-back-seat': '/assets/thumbnails/round-back-chair-seat-cushion.png',
  'round-back-bench': '/assets/thumbnails/round-back-chair-seat-cushion.png',
  't-shaped-bench-seat-cushion': '/assets/thumbnails/t-shaped-bench-seat-cushion.png',
  'l-shaped-bench-seat-cushion': '/assets/thumbnails/l-shaped-bench-seat-cushion.png',
  'waterfall-edge-bench-seat-cushion': '/assets/thumbnails/waterfall-edge-bench-seat-cushion.png',
  'waterfall-edge-cushion': '/assets/thumbnails/waterfall-edge-bench-seat-cushion.png',
  'waterfall-edge-back-cushion': '/assets/thumbnails/waterfall-edge-back-cushion.png',
  'scatter-cushion': '/assets/thumbnails/scatter-cushion.png',
  'bolster-cushion': '/assets/thumbnails/bolster-cushion.png',
  'bench-back-cushion': '/assets/thumbnails/waterfall-edge-back-cushion.png',
  'premium-cushion': '/assets/thumbnails/rectangle-bench-seat-cushion.png',
};

const PRODUCT_TYPE_LABELS = {
  'cover_and_foam': 'Cover & Foam Insert',
  'cover_only': 'Cover Only',
  'foam_only': 'Foam Insert Only',
};

const ZIP_LABELS = {
  'back': 'Back Edge',
  'front': 'Front Edge',
  'left': 'Left Edge',
  'right': 'Right Edge',
  'length': 'Along Length',
  'width': 'Along Width',
  'long-edge': 'Long Edge',
  'short-edge': 'Short Edge',
};

function formatDimensions(item) {
  const d = item.dimensions;
  if (!d) return null;
  const tid = item.cushion_type_id;
  const rows = [];

  if (tid === 'round-bench-seat-cushion') {
    if (d.diameter) rows.push({ label: 'Diameter', value: `${d.diameter} cm` });
    if (d.thickness) rows.push({ label: 'Thickness', value: `${d.thickness} cm` });
  } else if (tid === 'bolster-cushion') {
    if (d.length) rows.push({ label: 'Length', value: `${d.length} cm` });
    if (d.width) rows.push({ label: 'Diameter', value: `${d.width} cm` });
  } else if (tid === 'wedge-back-cushion') {
    if (d.length) rows.push({ label: 'Width', value: `${d.length} cm` });
    if (d.back_height) rows.push({ label: 'Back Height', value: `${d.back_height} cm` });
    if (d.base_depth) rows.push({ label: 'Base Depth', value: `${d.base_depth} cm` });
    if (d.top_depth) rows.push({ label: 'Top Depth', value: `${d.top_depth} cm` });
  } else if (tid === 'bay-window-bench-seat-cushion') {
    if (d.front_width) rows.push({ label: 'Front Width', value: `${d.front_width} cm` });
    if (d.back_width) rows.push({ label: 'Back Width', value: `${d.back_width} cm` });
    if (d.depth) rows.push({ label: 'Depth', value: `${d.depth} cm` });
    if (d.thickness) rows.push({ label: 'Thickness', value: `${d.thickness} cm` });
  } else if (tid === 'l-shaped-bench-seat-cushion' || tid === 't-shaped-bench-seat-cushion') {
    if (d.length) rows.push({ label: 'Long Width', value: `${d.length} cm` });
    if (d.width) rows.push({ label: 'Full Depth', value: `${d.width} cm` });
    if (d.back_width) rows.push({ label: 'Short Width', value: `${d.back_width} cm` });
    if (d.thickness) rows.push({ label: 'Thickness', value: `${d.thickness} cm` });
  } else if (tid === 'chair-bench-seat-cushion' || tid === 'round-back-chair-seat-cushion' || tid === 'round-back-seat') {
    if (d.length) rows.push({ label: 'Width', value: `${d.length} cm` });
    if (d.width) rows.push({ label: 'Full Depth', value: `${d.width} cm` });
    if (d.straight_depth) rows.push({ label: 'Short Depth', value: `${d.straight_depth} cm` });
    if (d.thickness) rows.push({ label: 'Thickness', value: `${d.thickness} cm` });
  } else {
    if (d.length) rows.push({ label: 'Length', value: `${d.length} cm` });
    if (d.width) rows.push({ label: 'Width', value: `${d.width} cm` });
    if (d.thickness) rows.push({ label: 'Thickness', value: `${d.thickness} cm` });
  }

  return rows;
}

const PIPING_LABELS = {
  'same': 'Same Material',
  'different': 'Different Material',
};

function CartItemDetails({ item }) {
  if (item.item_type === 'fabric-samples') return null;
  const dims = formatDimensions(item);
  const typeLabel = PRODUCT_TYPE_LABELS[item.product_type] || item.product_type;
  const zipLabel = item.zip_placement ? (ZIP_LABELS[item.zip_placement] || item.zip_placement) : null;
  const config = item.configuration;
  const pipingLabel = item.piping_option ? (PIPING_LABELS[item.piping_option] || item.piping_option) : null;

  return (
    <div className="mt-3 space-y-2" data-testid={`cart-item-details-${item.id}`}>
      {/* Dimensions */}
      {dims && dims.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
          {dims.map((d, i) => (
            <span key={i}><span className="text-gray-400">{d.label}:</span> {d.value}</span>
          ))}
        </div>
      )}

      {/* Tags - each on its own line */}
      <div className="flex flex-col gap-1.5">
        <span className="px-3 py-1 bg-cream rounded-full text-xs font-body text-gray-700 w-fit">{typeLabel}</span>
        {item.fabric_name && (
          <span className="px-3 py-1 bg-cream rounded-full text-xs font-body text-gray-700 w-fit">Fabric: {item.fabric_name}</span>
        )}
        {item.foam_name && (
          <span className="px-3 py-1 bg-cream rounded-full text-xs font-body text-gray-700 w-fit">Foam: {item.foam_name}</span>
        )}
        {zipLabel && (
          <span className="px-3 py-1 bg-cream rounded-full text-xs font-body text-gray-700 w-fit">Zip: {zipLabel}</span>
        )}
        {pipingLabel && (
          <span className="px-3 py-1 bg-cream rounded-full text-xs font-body text-gray-700 w-fit">
            Piping: {pipingLabel}{item.piping_fabric_name && item.piping_option === 'different' ? ` (${item.piping_fabric_name})` : ''}
          </span>
        )}
        {config?.facing && (
          <span className="px-3 py-1 bg-cream rounded-full text-xs font-body text-gray-700 w-fit">Facing: {config.facing}</span>
        )}
        {config?.addMiddle && (
          <span className="px-3 py-1 bg-cream rounded-full text-xs font-body text-gray-700 w-fit">Middle Piece: {config.middleWidth} cm</span>
        )}
      </div>
    </div>
  );
}

const CartPage = () => {
  const { cart, loading, removeFromCart, updateItemQuantity } = useCart();
  const navigate = useNavigate();

  const handleRemoveItem = async (itemId) => {
    await removeFromCart(itemId);
    toast.success('Item removed from cart');
  };

  if (loading) {
    return (
      <div className="pt-20 sm:pt-[122px] min-h-screen flex items-center justify-center bg-cream" data-testid="cart-loading">
        <Loader2 className="h-7 w-7 animate-spin text-teal-500" />
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="pt-20 sm:pt-[122px] min-h-screen bg-cream" data-testid="empty-cart">
        <div className="max-w-4xl mx-auto px-5 py-24 text-center">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-card">
            <ShoppingBag className="h-10 w-10 text-gray-300" />
          </div>
          <h1 className="text-3xl font-display font-semibold text-[#1A1A1A] mb-4">Your Cart is Empty</h1>
          <p className="text-gray-500 font-body mb-8">Start designing your perfect cushion today!</p>
          <Link to="/products">
            <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8 py-6 text-base font-body font-medium" data-testid="browse-products-btn">
              Browse Products
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 sm:pt-[122px] min-h-screen bg-cream" data-testid="cart-page">
      {/* Header */}
      <section className="bg-white py-10 sm:py-16 border-b border-cream-dark">
        <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
          <h1 className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight" data-testid="cart-title">
            Shopping Cart
          </h1>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl font-body">{cart.item_count} {cart.item_count === 1 ? 'item' : 'items'} in your cart.</p>
        </div>
      </section>

      <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12 py-6 sm:py-12">
        <div className="grid lg:grid-cols-3 gap-5 lg:gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2" data-testid="cart-items">
            {cart.items.map((item, index) => (
              <div key={item.id}>
                {index > 0 && <div className="my-2.5" />}
                <div
                  className="bg-white rounded-2xl p-6 shadow-card border border-gray-100"
                  data-testid={`cart-item-${item.id}`}
                >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-5">
                  <div className="w-full sm:w-20 sm:h-20 bg-cream rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0">
                    {item.preview_image ? (
                      <img src={`${API}${item.preview_image}`} alt={item.cushion_name} className="w-full h-auto rounded-xl" />
                    ) : item.cushion_type_id && CUSHION_THUMBNAILS[item.cushion_type_id] ? (
                      <img src={CUSHION_THUMBNAILS[item.cushion_type_id]} alt={item.cushion_name} className="w-full h-full object-cover" />
                    ) : (
                      <ShoppingBag className="h-8 w-8 text-teal-200" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <h3 className="font-display font-semibold text-lg text-[#1A1A1A]">{item.cushion_name}</h3>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveItem(item.id)}
                        className="text-gray-400 hover:text-white hover:bg-gray-800 flex-shrink-0"
                        data-testid={`remove-item-${item.id}`}
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    </div>

                    {/* Fabric samples display */}
                    {item.item_type === 'fabric-samples' && item.samples ? (
                      <div className="flex flex-wrap gap-3 mt-3">
                        {item.samples.map((s, i) => (
                          <div key={i} className="flex items-center gap-2 bg-cream rounded-xl p-1.5 pr-3">
                            {s.imageUrl ? (
                              <img src={s.imageUrl} alt={s.name} className="w-10 h-10 rounded-lg object-cover border border-gray-200" />
                            ) : (
                              <span className="w-10 h-10 rounded-lg bg-gray-200 border border-gray-200" />
                            )}
                            <div>
                              <p className="text-xs font-medium text-gray-800">{s.name}</p>
                              <p className="text-[10px] text-gray-500">{s.rangeName}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <CartItemDetails item={item} />
                    )}

                    <div className="mt-4">
                      {item.item_type === 'fabric-samples' ? (
                        <span className="text-sm font-semibold text-teal-600">FREE</span>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">Qty</span>
                            <button
                              onClick={() => updateItemQuantity(item.id, Math.max(1, item.quantity - 1))}
                              disabled={item.quantity <= 1}
                              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              data-testid={`qty-minus-${item.id}`}
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="w-8 text-center text-sm font-semibold" data-testid={`qty-value-${item.id}`}>{item.quantity}</span>
                            <button
                              onClick={() => updateItemQuantity(item.id, item.quantity + 1)}
                              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                              data-testid={`qty-plus-${item.id}`}
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <div className="mt-3 border-t border-b border-gray-200 py-3 flex items-baseline justify-between">
                            <span className="text-sm text-gray-500">Unit Price <span className="font-semibold text-gray-700">${item.unit_price.toFixed(0)}</span></span>
                            <span className="text-sm text-gray-500">Total <span className="font-semibold text-teal-600">${item.total_price.toFixed(0)}</span></span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              </div>
            ))}

            <Link to="/products" className="hidden lg:inline-flex items-center text-teal-600 hover:text-teal-700 font-medium font-body text-sm">
              <ArrowRight className="mr-2 h-4 w-4 rotate-180" />
              Continue Shopping
            </Link>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-8 shadow-card sticky top-24" data-testid="order-summary">
              <h2 className="text-xl font-display font-semibold text-[#1A1A1A] mb-6">Order Summary</h2>

              {/* Price Summary */}
              <div className="space-y-3">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal ({cart.item_count} {cart.item_count === 1 ? 'item' : 'items'})</span>
                  <span>${cart.subtotal.toFixed(0)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className="text-sm text-gray-400">Calculated at checkout</span>
                </div>
                <div className="flex justify-between text-2xl font-bold pt-3 border-t">
                  <span>Subtotal</span>
                  <span className="text-teal-600" data-testid="cart-total">
                    ${cart.subtotal.toFixed(0)}
                  </span>
                </div>
              </div>

              {/* Checkout Button */}
              <div className="mt-8">
                <Button
                  onClick={() => navigate('/checkout')}
                  disabled={cart.items.length === 0}
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white rounded-full py-6 text-base font-body"
                  data-testid="proceed-to-checkout-btn"
                >
                  <CreditCard className="mr-2 h-5 w-5" />
                  Proceed to Checkout
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="mt-8 pt-6 border-t">
                <div className="flex items-center justify-center gap-6 text-gray-400">
                  <FaCcStripe className="h-10 w-10" title="Stripe" />
                  <FaPaypal className="h-10 w-10" title="PayPal" />
                </div>
                <p className="text-xs text-center text-gray-500 mt-4">
                  Your payment information is encrypted and secure.
                </p>
              </div>
            </div>

            {/* Continue Shopping - below summary on mobile */}
            <div className="mt-5 lg:hidden">
              <Link to="/products" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium font-body text-sm">
                <ArrowRight className="mr-2 h-4 w-4 rotate-180" />
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
